import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type RefObject,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { ICONS, type IconName } from './icons.generated.js';

export type { IconName };

export function cx(...parts: (string | false | null | undefined | 0)[]) {
  return parts.filter(Boolean).join(' ');
}

/** Interface icon from the brand kit (24px grid, 1.75px strokes). Decorative unless labelled. */
export function Icon({ name, size = 20, label, className }: { name: IconName; size?: number; label?: string; className?: string }) {
  return (
    <svg
      className={cx('dw-icon', className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );
}

/** The selected folded-D mark, from the supplied production vector. */
export function LogoMark({ size = 24, tone = 'blue' }: { size?: number; tone?: 'blue' | 'white' | 'current' }) {
  const fill = tone === 'blue' ? '#155EEF' : tone === 'white' ? '#FFFFFF' : 'currentColor';
  return (
    <svg width={size} height={(size * 110) / 116} viewBox="0 0 116 110" aria-hidden="true" focusable="false">
      <path d="M8 6.3L41.1 25.4V80.9H43.2C68.5 80.9 84.9 68.4 86.5 50.5C96 56 104 66 106 76.3C102 92 86 104 66.3 104H8Z" fill={fill} />
      <path d="M10.4 4H70.7C92.2 4 108 20.5 108 44V66.7C104.5 58 98.4 53.1 90 48Z" fill={fill} />
    </svg>
  );
}

/** Mark + wordmark: DeskWorks in Inter Bold, One in Inter Regular. */
export function Logo({ tone = 'white', size = 22 }: { tone?: 'blue' | 'white' | 'current'; size?: number }) {
  return (
    <span className={cx('dw-logo', `dw-logo--${tone}`)} aria-label="DeskWorks One" role="img">
      <LogoMark size={size} tone={tone} />
      <span className="dw-logo__word" aria-hidden="true">
        <b>DeskWorks</b> One
      </span>
    </span>
  );
}

type ButtonVariant = 'primary' | 'secondary' | 'plain' | 'danger' | 'header';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: IconName;
  size?: 'sm' | 'md' | 'lg';
  busy?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', icon, size = 'md', busy, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx('dw-btn', `dw-btn--${variant}`, `dw-btn--${size}`, !children && 'dw-btn--icon-only', className)}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <Spinner size={16} /> : icon ? <Icon name={icon} size={size === 'sm' ? 16 : 18} /> : null}
      {children ? <span>{children}</span> : null}
    </button>
  );
});

/** Icon-only control. An accessible name is required. */
export const IconButton = forwardRef<HTMLButtonElement, Omit<ButtonProps, 'children'> & { label: string; icon: IconName; badge?: number }>(
  function IconButton({ label, icon, badge, variant = 'plain', className, ...rest }, ref) {
    return (
      <button ref={ref} type="button" aria-label={label} title={label} className={cx('dw-iconbtn', `dw-iconbtn--${variant}`, className)} {...rest}>
        <Icon name={icon} />
        {badge ? (
          <span className="dw-iconbtn__badge" aria-hidden="true">
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </button>
    );
  },
);

export function Spinner({ size = 20, label }: { size?: number; label?: string }) {
  return (
    <span className="dw-spinner" style={{ width: size, height: size }} role={label ? 'status' : undefined} aria-label={label}>
      <span />
    </span>
  );
}

/** A stable, pleasant hue for a person or mailbox, so the same sender always gets the same colour. */
/** OKLCH hue angles: equal perceived lightness, so white initials read the same on every colour. */
const AVATAR_HUES = [255, 268, 285, 302, 322, 345, 12, 32, 152, 172, 195, 222];

export function avatarHue(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_HUES[h % AVATAR_HUES.length]!;
}

export function Avatar({ initials, size = 32, guest, title, seed }: { initials: string; size?: number; guest?: boolean; title?: string; seed?: string }) {
  return (
    <span
      className={cx('dw-avatar', guest && 'dw-avatar--guest')}
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.36), ['--dw-avatar-hue' as string]: avatarHue((seed ?? initials).toLowerCase()) }}
      title={title}
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

export function Dot({ color, size = 8 }: { color: string; size?: number }) {
  return <span className="dw-dot" style={{ background: color, width: size, height: size }} aria-hidden="true" />;
}

export function CountBadge({ count, label }: { count: number; label?: string }) {
  if (!count) return null;
  return (
    <span className="dw-count" aria-label={label ?? `${count} unread`}>
      {count > 999 ? '999+' : count}
    </span>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="dw-empty">
      {icon ? (
        <span className="dw-empty__icon">
          <Icon name={icon} size={28} />
        </span>
      ) : null}
      <h2>{title}</h2>
      {children ? <p>{children}</p> : null}
      {action}
    </div>
  );
}

export function Banner({ tone = 'info', icon, children, action }: { tone?: 'info' | 'warning' | 'danger' | 'success'; icon?: IconName; children: ReactNode; action?: ReactNode }) {
  return (
    <div className={cx('dw-banner', `dw-banner--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={icon ?? (tone === 'success' ? 'check' : tone === 'info' ? 'info' : 'warning')} size={18} />
      <div className="dw-banner__body">{children}</div>
      {action}
    </div>
  );
}

/* ----------------------------------------------------------------- forms */

export const TextField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode; error?: string | null; hideLabel?: boolean }>(
  function TextField({ label, hint, error, hideLabel, id, className, ...rest }, ref) {
    const auto = useId();
    const fid = id ?? auto;
    return (
      <div className={cx('dw-field', error && 'dw-field--error', className)}>
        <label htmlFor={fid} className={cx(hideLabel && 'dw-visually-hidden')}>
          {label}
        </label>
        <input ref={ref} id={fid} aria-invalid={!!error || undefined} aria-describedby={hint || error ? `${fid}-hint` : undefined} {...rest} />
        {error ? (
          <span id={`${fid}-hint`} className="dw-field__error">
            {error}
          </span>
        ) : hint ? (
          <span id={`${fid}-hint`} className="dw-field__hint">
            {hint}
          </span>
        ) : null}
      </div>
    );
  },
);

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { label: string; hideLabel?: boolean }>(
  function TextArea({ label, hideLabel, id, className, ...rest }, ref) {
    const auto = useId();
    const fid = id ?? auto;
    return (
      <div className={cx('dw-field', className)}>
        <label htmlFor={fid} className={cx(hideLabel && 'dw-visually-hidden')}>
          {label}
        </label>
        <textarea ref={ref} id={fid} {...rest} />
      </div>
    );
  },
);

export function SelectField({ label, hideLabel, id, className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label: string; hideLabel?: boolean }) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <div className={cx('dw-field', className)}>
      <label htmlFor={fid} className={cx(hideLabel && 'dw-visually-hidden')}>
        {label}
      </label>
      <select id={fid} {...rest}>
        {children}
      </select>
    </div>
  );
}

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; description?: string; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="dw-switch">
      <div>
        <label htmlFor={id}>{label}</label>
        {description ? <p className="dw-switch__desc">{description}</p> : null}
      </div>
      <button id={id} type="button" role="switch" aria-checked={checked} disabled={disabled} className="dw-switch__track" onClick={() => onChange(!checked)}>
        <span className="dw-switch__thumb" />
      </button>
    </div>
  );
}

/** Keeps an absolutely positioned indicator under the active child of a container. */
function useIndicator(container: RefObject<HTMLElement | null>, selector: string, deps: unknown[]) {
  const [box, setBox] = useState<{ x: number; w: number } | null>(null);
  useLayoutEffect(() => {
    const el = container.current;
    if (!el) return;
    const measure = () => {
      const active = el.querySelector<HTMLElement>(selector);
      setBox(active ? { x: active.offsetLeft, w: active.offsetWidth } : null);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return box;
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string; icon?: IconName }[]; onChange: (v: T) => void; label: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const box = useIndicator(ref, 'button.is-active', [value, options.length]);
  return (
    <div className="dw-segmented" role="radiogroup" aria-label={label} ref={ref}>
      {box ? <span className="dw-segmented__thumb" aria-hidden="true" style={{ width: box.w, transform: `translateX(${box.x}px)` }} /> : null}
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={cx(value === o.value && 'is-active')} onClick={() => onChange(o.value)}>
          {o.icon ? <Icon name={o.icon} size={16} /> : null}
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export function Tabs<T extends string>({ value, tabs, onChange, label }: { value: T; tabs: { value: T; label: string; count?: number }[]; onChange: (v: T) => void; label: string }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const wrap = useRef<HTMLDivElement>(null);
  const box = useIndicator(wrap, '.dw-tabs__tab.is-active', [value, tabs.length]);
  return (
    <div className="dw-tabs" role="tablist" aria-label={label} ref={wrap}>
      {box ? <span className="dw-tabs__ink" aria-hidden="true" style={{ width: box.w, transform: `translateX(${box.x}px)` }} /> : null}
      {tabs.map((t, i) => (
        <button
          key={t.value}
          ref={(el) => {
            refs.current[i] = el;
          }}
          role="tab"
          type="button"
          aria-selected={value === t.value}
          tabIndex={value === t.value ? 0 : -1}
          className={cx('dw-tabs__tab', value === t.value && 'is-active')}
          onClick={() => onChange(t.value)}
          onKeyDown={(e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            const next = (i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length;
            refs.current[next]?.focus();
            onChange(tabs[next]!.value);
          }}
        >
          {t.label}
          {t.count ? <span className="dw-tabs__count">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------- overlays */

function useFocusTrap(active: boolean, onEscape: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  // Keep the latest handler without re-running the trap (which would steal focus on every render).
  const escape = useRef(onEscape);
  escape.current = onEscape;
  useEffect(() => {
    if (!active) return;
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusables = () =>
      node ? [...node.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.hasAttribute('disabled')) : [];
    const first = node?.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0];
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        escape.current();
      }
      if (e.key !== 'Tab') return;
      const list = focusables();
      if (!list.length) return;
      const firstEl = list[0]!;
      const lastEl = list[list.length - 1]!;
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    node?.addEventListener('keydown', onKey);
    return () => {
      node?.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [active]);
  return ref;
}

export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
  size = 'md',
  description,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
  description?: string;
}) {
  const titleId = useId();
  const [closing, setClosing] = useState(false);
  // Play the exit animation before the caller unmounts the dialog.
  const close = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return onClose();
    setClosing(true);
    window.setTimeout(onClose, 150);
  }, [onClose]);
  useEffect(() => {
    if (open) setClosing(false);
  }, [open]);
  const ref = useFocusTrap(open, close);
  if (!open) return null;
  return createPortal(
    <div className={cx('dw-overlay', closing && 'is-closing')} onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div ref={ref} className={cx('dw-dialog', `dw-dialog--${size}`)} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="dw-dialog__head">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          <IconButton icon="close" label="Close" onClick={close} />
        </header>
        <div className="dw-dialog__body">{children}</div>
        {footer ? <footer className="dw-dialog__foot">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export interface MenuItem {
  label: string;
  icon?: IconName;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
}

/** Accessible popover menu with arrow-key navigation. */
export function Menu({ trigger, items, align = 'end' }: { trigger: (props: { onClick: () => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => ReactNode; items: MenuItem[]; align?: 'start' | 'end' }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>('[role=menuitem]:not([disabled])')?.focus();
    const onDoc = (e: MouseEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className="dw-menu" ref={wrap}>
      {trigger({ onClick: () => setOpen((o) => !o), 'aria-expanded': open, 'aria-haspopup': 'menu' })}
      {open ? (
        <div
          ref={list}
          role="menu"
          className={cx('dw-menu__list', `dw-menu__list--${align}`)}
          onKeyDown={(e) => {
            const els = [...(list.current?.querySelectorAll<HTMLElement>('[role=menuitem]:not([disabled])') ?? [])];
            const i = els.indexOf(document.activeElement as HTMLElement);
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              els[(i + 1) % els.length]?.focus();
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              els[(i - 1 + els.length) % els.length]?.focus();
            } else if (e.key === 'Escape' || e.key === 'Tab') {
              close();
            }
          }}
        >
          {items.map((it) => (
            <button
              key={it.label}
              role="menuitem"
              type="button"
              disabled={it.disabled}
              className={cx('dw-menu__item', it.danger && 'is-danger')}
              onClick={() => {
                close();
                it.onSelect();
              }}
            >
              {it.icon ? <Icon name={it.icon} size={18} /> : <span style={{ width: 18 }} />}
              {it.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------------------- toasts */

export interface ToastItem {
  id: number;
  message: string;
  tone?: 'info' | 'success' | 'warning' | 'danger';
  action?: { label: string; onClick: () => void };
}

let toastId = 0;
const toastListeners = new Set<(t: ToastItem[]) => void>();
let toasts: ToastItem[] = [];

export function toast(message: string, opts: Omit<ToastItem, 'id' | 'message'> & { duration?: number } = {}) {
  const item: ToastItem = { id: ++toastId, message, ...opts };
  toasts = [...toasts, item].slice(-4);
  toastListeners.forEach((l) => l(toasts));
  setTimeout(() => dismissToast(item.id), opts.duration ?? (opts.action ? 8000 : 4500));
}

export function dismissToast(id: number) {
  toasts = toasts.filter((t) => t.id !== id);
  toastListeners.forEach((l) => l(toasts));
}

export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>(toasts);
  useEffect(() => {
    toastListeners.add(setItems);
    return () => void toastListeners.delete(setItems);
  }, []);
  return (
    <div className="dw-toaster" role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={cx('dw-toast', t.tone && `dw-toast--${t.tone}`)}>
          <span>{t.message}</span>
          {t.action ? (
            <button
              type="button"
              onClick={() => {
                t.action!.onClick();
                dismissToast(t.id);
              }}
            >
              {t.action.label}
            </button>
          ) : null}
          <button type="button" aria-label="Dismiss" className="dw-toast__close" onClick={() => dismissToast(t.id)}>
            <Icon name="close" size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function VisuallyHidden({ children }: { children: ReactNode }) {
  return <span className="dw-visually-hidden">{children}</span>;
}
